import { HttpClient } from '@angular/common/http';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';
import { Injectable, inject } from '@angular/core';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { HttpMethods } from '@cbre/http';
import { compose } from '@shared/utils/helpers/compose';
import { forkJoin, of, Observable } from 'rxjs';
import { catchError, map, share, shareReplay } from 'rxjs/operators';

export interface LookUpValue {
  string: string;
}

export interface ColumnFields {
  columnId?: number;
  field: string;
  entity?: string;
  update?: string;
  lookupCategory?: string;
  lovDefaultPayload?: any;
}

@Injectable({
  providedIn: 'root',
})
export class LookupCategoryService extends HttpMethods {
  private readonly logger: CbreLogger = inject(CbreLoggerToken);

  constructor(
    http: HttpClient,
    private userInfoResolver: UserInfoResolver
  ) {
    super(http);
  }

  private streams: Map<string, Observable<any>> = new Map();

  private tokenReplacer = compose(
    url => url.replace(/:userId/g, this.userInfoResolver.resolve().id.toString()),
    url => url.replace(/POST:/g, '')
  );

  getLookupItems(displayColumn: ColumnFields[]) {
    if (!displayColumn.length) {
      return of([]);
    }

    const lookUpRequests = displayColumn.map(({ field, lookupCategory, lovDefaultPayload }) => {
      if (lookupCategory.includes('?all=false') || lookupCategory.includes('POST:')) {
        return this.httpPost(this.tokenReplacer(lookupCategory), lovDefaultPayload ?? {}).pipe(
          map(res => ({ [field]: res })),
          catchError(this.catchLookupQueryError.bind(null, field))
        );
      } else if (lookupCategory.includes(',')) {
        /*
         * NOTE: if lookupCategory includes a comma, it means that there are
         * more than one set of dropdown values for the same field, therefore
         * the usage of the index (`${field}_${index + 1}`) to set the field name.
         * Please refer to [[OffMarketReasonLookupEnum]] as an example.
         */
        return forkJoin(lookupCategory.split(',').map((lookup, index) => this.query(`${field}_${index + 1}`, lookup)));
      } else {
        return this.query(field, lookupCategory);
      }
    });
    return forkJoin(lookUpRequests).pipe(
      // NOTE: map operator used the multiple lookups scenario on [[LookupCategoryService.getLookupItems]]
      map(columns =>
        columns.reduce((acc, column) => (Array.isArray(column) ? [...acc, ...column] : [...acc, column]), [])
      )
    );
  }

  getGeoLookupItems(displayColumn: ColumnFields[], type?: string): Observable<any> {
    if (!displayColumn.length) {
      return of([]);
    }

    displayColumn = displayColumn.filter(
      product => product.lookupCategory.includes('?all=false') && (!type || !product.field.includes('target_geography'))
    );

    const lookupRequests = displayColumn.map(res => this.httpPost(this.tokenReplacer(res.lookupCategory), {}));
    return forkJoin(lookupRequests);
  }

  targetGet(url) {
    return this.httpGet(url);
  }

  targetPost(url, value) {
    return this.httpPost(url, value);
  }

  query(field: string, lookupCategory: string): Observable<any> {
    return this.getStream(lookupCategory, field);
  }

  private getStream(url, field) {
    if (!this.streams.has(url)) {
      this.createStream(url);
    }
    return this.streams.get(url).pipe(
      // a copy per caller: cached LOVs are shared for the session and callers may mutate them.
      // JSON round trip: the payload is JSON anyway, and structuredClone is missing in Jest's jsdom.
      map(res => ({ [field]: JSON.parse(JSON.stringify(res)) })),
      catchError(this.catchLookupQueryError.bind(null, field))
    );
  }

  /**
   * Reference LOVs (`/lookup/*`) are cached for the session. `shareReplay` resets on error
   * (unlike HttpStreamsService), so a failed lookup is fetched again on the next call.
   * Anything else (e.g. the user's own tags from `/search/tags`) is still fetched per call.
   */
  private createStream(url: string) {
    const cache = url.startsWith('/lookup/') ? shareReplay(1) : share();
    this.streams.set(url, this.httpGet(url).pipe(cache));
  }

  private catchLookupQueryError(field) {
    this.logger.warn(`Some lookup for field ${field} options were not able to be fetched`);
    return of([]);
  }
}
